/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

function UsersKhususLaporanSingleDate() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tanggal, setTanggal] = useState("");
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Modal kegiatan (mirror of laporanDinas.js behavior)
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);

  // Set default date on component mount
  useEffect(() => {
    const today = new Date();
    setTanggal(today.toISOString().split("T")[0]);

    // Auto-load data with default date
    fetchData(today.toISOString().split("T")[0]);
  }, []);

  const fetchData = async (tanggal) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/users-khusus/tanggal/${tanggal}`
      );

      if (response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data laporan harian users khusus",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Open kegiatan modal when clicking "Sudah Lapor"
  const handleOpenKegiatanModal = (pegawai) => {
    if (pegawai.status_laporan !== "Sudah Lapor") {
      swal("Info", "Pegawai belum membuat laporan pada tanggal ini", "info");
      return;
    }

    if (!pegawai.id_laporan) {
      swal("Error", "Data laporan tidak ditemukan", "error");
      return;
    }

    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan ${pegawai.nama_pegawai} - ${new Date(
        tanggal
      ).toLocaleDateString("id-ID")}`
    );

    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${pegawai.id_laporan}`)
      .then((res) => {
        const kegiatanData = res.data?.kegiatan || [];
        setSelectedKegiatan(kegiatanData);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        swal("Error", "Gagal mengambil data kegiatan", "error");
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const handleGetData = () => {
    if (!tanggal) {
      swal({
        title: "Error",
        text: "Tanggal harus diisi",
        icon: "error",
      });
      return;
    }

    fetchData(tanggal);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleExportExcel = () => {
    if (!data || !data.data_pegawai) {
      swal({
        title: "Error",
        text: "Tidak ada data untuk diekspor",
        icon: "error",
      });
      return;
    }

    try {
      // Create HTML table
      let tableHTML = `
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Nama Pegawai</th>
              <th>Jabatan</th>
              <th>Dinas</th>
              <th>Bidang</th>
              <th>Pangkat</th>
              <th>Status Laporan</th>
              <th>Jumlah Kegiatan</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.data_pegawai.forEach((pegawai, index) => {
        tableHTML += `
          <tr>
            <td>${index + 1}</td>
            <td>${pegawai.nama_pegawai}</td>
            <td>${pegawai.nama_jabatan}</td>
            <td>${pegawai.nama_dinas}</td>
            <td>${pegawai.nama_bidang}</td>
            <td>${pegawai.pangkat}</td>
            <td>${pegawai.status_laporan}</td>
            <td>${pegawai.jumlah_kegiatan}</td>
          </tr>
        `;
      });

      tableHTML += `
          </tbody>
        </table>
      `;

      // Convert to Excel
      const blob = new Blob([tableHTML], { type: "application/vnd.ms-excel" });
      const fileName = `Laporan_Harian_Users_Khusus_${tanggal}.xls`;
      saveAs(blob, fileName);

      swal({
        title: "Success",
        text: "Data berhasil diekspor ke Excel",
        icon: "success",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      swal({
        title: "Error",
        text: "Gagal mengekspor data",
        icon: "error",
      });
    }
  };

  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "20%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "15%" },
    {
      Header: "Nama Dinas",
      accessor: "nama_dinas",
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => (
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            width: "200px",
            maxWidth: "200px",
            minWidth: "200px",
            display: "block",
          }}
        >
          {value}
        </MDTypography>
      ),
    },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    {
      Header: "Status Laporan",
      accessor: "status_laporan",
      width: "12%",
      Cell: ({ row, value }) => (
        <Chip
          label={value}
          color={value === "Sudah Lapor" ? "success" : "warning"}
          size="small"
          onClick={() => handleOpenKegiatanModal(row.original)}
          sx={{ cursor: "pointer" }}
        />
      ),
    },
    { Header: "Jumlah Kegiatan", accessor: "jumlah_kegiatan", width: "13%" },
  ];

  const rows =
    data?.data_pegawai?.map((pegawai, index) => ({
      no: index + 1,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan,
      nama_dinas: pegawai.nama_dinas,
      nama_bidang: pegawai.nama_bidang,
      profil: pegawai.profil,
      pangkat: pegawai.pangkat,
      status_laporan: pegawai.status_laporan,
      id_laporan: pegawai.id_laporan || null,
      jumlah_kegiatan: pegawai.jumlah_kegiatan,
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDTypography variant="h6" gutterBottom>
                    Statistik Laporan Harian Pimpinan
                  </MDTypography>
                  <MDBox>
                    <MDInput
                      type="date"
                      label="Pilih Tanggal"
                      value={tanggal}
                      onChange={(e) => setTanggal(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </MDBox>
                </MDBox>

                {data && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Users Khusus (Pimpinan Daerah dan Perangkat Daerah) -{" "}
                      {new Date(data.tanggal).toLocaleDateString("id-ID")}
                    </MDTypography>

                    <Grid container spacing={3} mt={2}>
                      <Grid item xs={12} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="info">
                            {data.statistik.total_pegawai}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="success">
                            {data.statistik.sudah_lapor}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Sudah Lapor
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="warning">
                            {data.statistik.belum_lapor}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Belum Lapor
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="primary">
                            {data.statistik.total_kegiatan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Kegiatan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Persentase Kepatuhan
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            data.statistik.persentase_kepatuhan >= 80
                              ? "success"
                              : data.statistik.persentase_kepatuhan >= 60
                              ? "warning"
                              : "error"
                          }
                        >
                          {data.statistik.persentase_kepatuhan}%
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={data.statistik.persentase_kepatuhan}
                        color={
                          data.statistik.persentase_kepatuhan >= 80
                            ? "success"
                            : data.statistik.persentase_kepatuhan >= 60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {data && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(data.tanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {data.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card key={`${row.no || "row"}-${index}`}>
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid_RSS}.jpg`}
                                      alt={row.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {row.nama_pegawai}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_jabatan}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_dinas}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_bidang} • {row.pangkat}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={row.status_laporan}
                                      color={
                                        row.status_laporan === "Sudah Lapor"
                                          ? "success"
                                          : "warning"
                                      }
                                      size="small"
                                      onClick={() =>
                                        handleOpenKegiatanModal(row)
                                      }
                                      sx={{ cursor: "pointer" }}
                                    />
                                    <Chip
                                      label={`Kegiatan: ${row.jumlah_kegiatan}`}
                                      color="info"
                                      size="small"
                                    />
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data laporan harian users khusus...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!data && !loading && tanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data laporan harian users khusus
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal lain atau periksa kembali data
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      {/* Modal Kegiatan */}
      <Dialog
        open={openKegiatanModal}
        onClose={handleCloseKegiatanModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            {loadingKegiatan ? (
              <MDBox textAlign="center" py={4}>
                <MDTypography variant="body1" color="text">
                  Memuat data kegiatan...
                </MDTypography>
              </MDBox>
            ) : !Array.isArray(selectedKegiatan) ||
              selectedKegiatan.length === 0 ? (
              <MDBox textAlign="center" py={4}>
                <MDTypography variant="body1" color="text">
                  Tidak ada kegiatan untuk ditampilkan
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {selectedKegiatan.map((kegiatan, index) => (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <MDBox>
                        <MDTypography variant="h6" color="info" gutterBottom>
                          Kegiatan {index + 1}
                        </MDTypography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <MDTypography
                              variant="subtitle2"
                              color="text"
                              gutterBottom
                            >
                              Deskripsi Kegiatan:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {kegiatan.kegiatan}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12}>
                            <MDTypography
                              variant="subtitle2"
                              color="text"
                              gutterBottom
                            >
                              Output:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {kegiatan.output}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6}>
                            <MDTypography
                              variant="subtitle2"
                              color="text"
                              gutterBottom
                            >
                              Jumlah Output:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {kegiatan.jumlah_output}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={6}>
                            <MDTypography
                              variant="subtitle2"
                              color="text"
                              gutterBottom
                            >
                              Satuan Output:
                            </MDTypography>
                            <MDTypography variant="body2" color="text">
                              {kegiatan.satuan_output}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
            Tutup
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersKhususLaporanSingleDate;
